import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';

export default function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <main className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="mb-12">
            <p className="font-manrope text-sm text-primary font-semibold uppercase tracking-widest mb-3">
              Legal
            </p>
            <h1 className="font-oswald uppercase text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              Terms &amp; Conditions
            </h1>
            <p className="font-manrope text-sm text-gray-500 mt-4">
              Last Updated: June 27, 2025
            </p>
            <div className="w-16 h-1 bg-primary mt-6" />
          </div>

          {/* Content */}
          <div className="space-y-8">
            <p className="font-manrope text-gray-700 leading-relaxed">
              Please read these Terms and Conditions ("Terms") carefully before using{' '}
              <strong>looseleafagency.com</strong> or purchasing any digital products from Loose Leaf Media ("we," "us," or
              "our"). By accessing our website or completing a purchase, you agree to be bound by these Terms. If you do not
              agree, please do not use our Site or services.
            </p>

            <Section title="1. About Us">
              <div className="font-manrope text-gray-700 space-y-1">
                <p><strong>Business Name:</strong> Loose Leaf Media</p>
                <p><strong>Website:</strong> looseleafagency.com</p>
                <p>
                  <strong>Contact Email:</strong>{' '}
                  <a href="mailto:hello@looseleafagency.com" className="text-primary hover:text-primary/80 transition-colors">
                    hello@looseleafagency.com
                  </a>
                </p>
              </div>
              <p className="font-manrope text-gray-700 leading-relaxed mt-3">
                We create and sell digital products — including eBooks and AI prompt guides — through standalone landing pages
                hosted on our domain.
              </p>
            </Section>

            <Section title="2. Eligibility">
              <p className="font-manrope text-gray-700 leading-relaxed">
                By using our Site and purchasing our products, you confirm that:
              </p>
              <ul className="list-disc pl-6 space-y-1 font-manrope text-gray-700 mt-3">
                <li>You are at least 18 years of age (or the age of legal majority in your jurisdiction)</li>
                <li>You have the legal capacity to enter into a binding agreement</li>
                <li>You will use our products for lawful purposes only</li>
                <li>You are not located in a country subject to applicable trade sanctions</li>
              </ul>
            </Section>

            <Section title="3. Digital Products">
              <Subsection title="3.1 Nature of Products">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  All products sold on our Site are digital goods delivered electronically. Upon successful payment, you will
                  receive a download link via email or be redirected to a secure download page. No physical goods will be shipped.
                </p>
              </Subsection>
              <Subsection title="3.2 Licence Grant">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  Upon purchase, we grant you a non-exclusive, non-transferable, personal licence to access and use the purchased
                  digital product for your own personal and professional purposes. You may not:
                </p>
                <ul className="list-disc pl-6 space-y-1 font-manrope text-gray-700 mt-2">
                  <li>Resell, redistribute, sublicense, or share the product with third parties</li>
                  <li>Repackage or re-brand the product as your own</li>
                  <li>Upload the product to file-sharing platforms, torrents, or public repositories</li>
                  <li>Use the product in any way that competes with or damages our business</li>
                </ul>
              </Subsection>
              <Subsection title="3.3 Delivery">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  Digital products are typically delivered immediately upon payment confirmation. If you do not receive your
                  download link within 24 hours, please contact us. We are not responsible for delivery failures caused by
                  incorrect email addresses provided by the customer.
                </p>
              </Subsection>
              <Subsection title="3.4 Access">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  We recommend downloading and saving your purchased product immediately. Download links may expire. We are not
                  obligated to provide indefinite access to download links beyond a reasonable period after purchase.
                </p>
              </Subsection>
            </Section>

            <Section title="4. Pricing and Payment">
              <Subsection title="4.1 Prices">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  All prices are displayed in US Dollars (USD) unless otherwise stated. Prices are subject to change without
                  notice. The price applicable to your purchase is the price displayed at the time of checkout.
                </p>
              </Subsection>
              <Subsection title="4.2 Payment Processing">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  All payments are processed securely by Stripe, Inc. We do not store your payment card information on our
                  servers. By making a purchase, you agree to Stripe's Terms of Service and Privacy Policy.
                </p>
              </Subsection>
              <Subsection title="4.3 Taxes">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  Prices displayed do not include applicable taxes. Depending on your location, local sales tax, VAT, or GST may
                  be added at checkout. You are responsible for any taxes applicable to your purchase under local law.
                </p>
              </Subsection>
              <Subsection title="4.4 Failed Payments">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  If your payment is declined or reversed, you will not receive or retain access to the digital product. Please
                  ensure your payment method is valid before completing a purchase.
                </p>
              </Subsection>
            </Section>

            <Section title="5. Refund and Money-Back Policy">
              <Subsection title="5.1 30-Day Money-Back Guarantee">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  We offer a 30-day money-back guarantee on all digital product purchases. If you are not satisfied with your
                  purchase for any reason, you may request a full refund within 30 days of your purchase date.
                </p>
              </Subsection>
              <Subsection title="5.2 How to Request a Refund">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  To request a refund, email us at{' '}
                  <a href="mailto:hello@looseleafagency.com" className="text-primary hover:text-primary/80 transition-colors">
                    hello@looseleafagency.com
                  </a>{' '}
                  within 30 days of purchase. Please include your order details (name, email used to purchase, and date of
                  purchase). We will process your refund within 5–10 business days.
                </p>
              </Subsection>
              <Subsection title="5.3 No Questions Asked">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  We do not require you to provide a reason for your refund request. We will process all valid refund requests
                  submitted within the 30-day window without question.
                </p>
              </Subsection>
              <Subsection title="5.4 After 30 Days">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  Refund requests submitted more than 30 days after purchase will not be accepted. All sales beyond this period
                  are final.
                </p>
              </Subsection>
              <Subsection title="5.5 Chargebacks">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  If you initiate a chargeback with your bank or card provider without first contacting us, we reserve the right
                  to dispute the chargeback and provide evidence of delivery and our refund policy. We encourage you to contact
                  us first — we will always work to resolve issues promptly.
                </p>
              </Subsection>
            </Section>

            <Section title="6. Intellectual Property">
              <p className="font-manrope text-gray-700 leading-relaxed">
                All content on our Site and within our digital products — including text, graphics, prompts, frameworks, and
                structure — is the intellectual property of <strong>Loose Leaf Media</strong> and is protected by applicable
                copyright and intellectual property laws.
              </p>
              <p className="font-manrope text-gray-700 leading-relaxed mt-3">
                You may not reproduce, publish, distribute, or create derivative works from our content without our prior written
                permission. The personal licence granted to you under Section 3.2 does not transfer any intellectual property
                rights.
              </p>
            </Section>

            <Section title="7. Disclaimers">
              <Subsection title="7.1 No Earnings Guarantee">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  Our digital products provide tools, frameworks, and prompts to improve productivity and content quality. We
                  make no guarantees, representations, or warranties regarding specific business outcomes, revenue, income, or
                  results. Individual results will vary based on your efforts, skills, and circumstances.
                </p>
              </Subsection>
              <Subsection title='7.2 "As Is" Basis'>
                <p className="font-manrope text-gray-700 leading-relaxed">
                  Our Site and products are provided on an "as is" and "as available" basis without warranties of any kind,
                  either express or implied.
                </p>
              </Subsection>
              <Subsection title="7.3 AI Tools">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  Our products provide prompts designed for use with third-party AI tools (such as ChatGPT, Claude, Gemini, and
                  others). We do not control these third-party platforms and are not responsible for changes to their services,
                  features, pricing, or availability. Results may vary based on the AI tool and version used.
                </p>
              </Subsection>
              <Subsection title="7.4 Accuracy">
                <p className="font-manrope text-gray-700 leading-relaxed">
                  While we make every effort to ensure our content is accurate and up to date, we do not warrant that all
                  information in our products is complete, accurate, or current at the time of purchase or use.
                </p>
              </Subsection>
            </Section>

            <Section title="8. Limitation of Liability">
              <p className="font-manrope text-gray-700 leading-relaxed">
                To the fullest extent permitted by applicable law, Loose Leaf Media, its owners, employees, affiliates, and
                service providers shall not be liable for any indirect, incidental, special, consequential, or punitive damages
                arising from your use of our Site or products, including but not limited to loss of profits, data, or business
                opportunities.
              </p>
              <p className="font-manrope text-gray-700 leading-relaxed mt-3">
                Our total liability to you for any claim arising from your purchase or use of our products shall not exceed the
                amount you paid for the specific product giving rise to the claim.
              </p>
            </Section>

            <Section title="9. Prohibited Use">
              <p className="font-manrope text-gray-700 leading-relaxed">You agree not to use our Site or products to:</p>
              <ul className="list-disc pl-6 space-y-1 font-manrope text-gray-700 mt-3">
                <li>Violate any applicable law or regulation</li>
                <li>Infringe upon any third party's intellectual property rights</li>
                <li>Upload or transmit malicious code, viruses, or harmful content</li>
                <li>Engage in fraudulent activity or misrepresentation</li>
                <li>Attempt to reverse engineer, copy, or reproduce our products for commercial distribution</li>
                <li>Harass, threaten, or harm any person</li>
              </ul>
            </Section>

            <Section title="10. Third-Party Services">
              <p className="font-manrope text-gray-700 leading-relaxed">
                Our Site uses third-party services including Stripe (payment processing), Google Analytics (analytics), Google
                Ads (advertising), and email service providers. Use of these services is subject to their respective terms and
                privacy policies. We are not responsible for the actions or practices of these third parties.
              </p>
            </Section>

            <Section title="11. Advertising">
              <p className="font-manrope text-gray-700 leading-relaxed">
                We may use Google Ads, Meta Ads, and other advertising platforms to promote our products. Our advertising
                practices comply with the policies of these platforms. We do not make misleading claims in our advertising. All
                advertised features, pricing, and guarantees are as described on our Site at the time of purchase.
              </p>
            </Section>

            <Section title="12. Governing Law and Disputes">
              <p className="font-manrope text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with applicable law. Any disputes arising from these
                Terms or your use of our Site shall first be attempted to be resolved amicably by contacting us directly. If a
                resolution cannot be reached, disputes shall be subject to the jurisdiction of the courts applicable to our
                business location.
              </p>
              <p className="font-manrope text-gray-700 leading-relaxed mt-3">
                If you are a consumer in the EU or UK, you retain the benefit of any mandatory consumer protection provisions
                applicable in your country of residence.
              </p>
            </Section>

            <Section title="13. Changes to These Terms">
              <p className="font-manrope text-gray-700 leading-relaxed">
                We reserve the right to update these Terms at any time. Changes will be effective upon posting to this page with
                a revised "Last Updated" date. Continued use of our Site or products after changes are posted constitutes your
                acceptance of the revised Terms.
              </p>
            </Section>

            <Section title="14. Entire Agreement">
              <p className="font-manrope text-gray-700 leading-relaxed">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and us with respect to
                your use of our Site and purchase of our digital products. They supersede any prior agreements or
                representations.
              </p>
            </Section>

            <Section title="15. Contact Us">
              <p className="font-manrope text-gray-700 leading-relaxed">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="mt-4 font-manrope text-gray-700 space-y-1">
                <p><strong>Business Name:</strong> Loose Leaf Media</p>
                <p><strong>Website:</strong> looseleafagency.com</p>
                <p>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:hello@looseleafagency.com" className="text-primary hover:text-primary/80 transition-colors">
                    hello@looseleafagency.com
                  </a>
                </p>
              </div>
            </Section>

            <div className="border-t border-gray-200 pt-8 mt-8">
              <p className="font-manrope text-sm text-gray-500">
                © 2026 Loose Leaf Media. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-gray-200 pt-8">
      <h2 className="font-oswald uppercase text-xl font-bold text-gray-900 tracking-wide mb-4">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Subsection({ title, children }) {
  return (
    <div className="mt-4">
      <h3 className="font-manrope text-sm font-bold text-gray-900 mb-2">{title}</h3>
      {children}
    </div>
  );
}
